import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { globalSearch, getSettings, updateSetting, submitSupportTicket } from '../services/api';
import { debounce } from 'lodash';
import { showSuccess, showError } from '../services/swal';

const staticPages = [
    { name: 'Dashboard', path: '/' },
    { name: 'POS / Billing', path: '/pos' },
    { name: 'Inventory', path: '/inventory' },
    { name: 'Categories', path: '/categories' },
    { name: 'Customers', path: '/customers' },
    { name: 'My Profile', path: '/profile' },
    { name: 'Account Settings', path: '/account-settings' },
    { name: 'Activity Log', path: '/activity-log' },
    { name: 'Settings', path: '/settings' },
    { name: 'Payroll & HR', path: '/employee-salary' },
];

const Layout = ({ children, title }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Safely parse user from localStorage
    const [user, setUser] = useState(() => {
        try {
            const storedUser = localStorage.getItem('user');
            if (!storedUser || storedUser === "undefined") return null;
            return JSON.parse(storedUser);
        } catch (e) {
            console.error("Failed to parse user from localStorage", e);
            return null;
        }
    });

    const [showSidebar, setShowSidebar] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        return localStorage.getItem('sidebar_collapsed') === 'true';
    });
    const [showDropdown, setShowDropdown] = useState(false);
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');

    useEffect(() => {
        localStorage.setItem('sidebar_collapsed', isSidebarCollapsed);
    }, [isSidebarCollapsed]);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('theme') || 'light';
        setTheme(savedTheme);
        document.documentElement.setAttribute('data-bs-theme', savedTheme);
    }, []);

    const dropdownRef = useRef(null);
    const searchRef = useRef(null);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('theme_toggled');
        navigate('/login');
    };

    const handleSupportSubmit = async () => {
        if (!supportForm.category || !supportForm.subject || !supportForm.message) {
            showError('Missing Fields', 'Please fill in all required fields.');
            return;
        }
        setSupportLoading(true);
        try {
            await submitSupportTicket(supportForm);
            showSuccess('Ticket Submitted!', 'Our team will get back to you shortly.');
            setShowSupportModal(false);
            setSupportForm({ category: '', subject: '', message: '' });
        } catch (err) {
            showError('Failed to Submit', err.response?.data?.message || 'Please try again later.');
        } finally {
            setSupportLoading(false);
        }
    };

    // Contact Support Modal
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [supportForm, setSupportForm] = useState({ category: '', subject: '', message: '' });
    const [supportLoading, setSupportLoading] = useState(false);

    const toggleTheme = async () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);
        sessionStorage.setItem('theme_toggled', 'true');
        document.documentElement.setAttribute('data-bs-theme', newTheme);
        window.dispatchEvent(new Event('localStorageChanged'));

        try {
            await updateSetting({ key: 'theme', value: newTheme });
        } catch (err) {
            console.error("Failed to sync theme with server", err);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
        debounce(async (query) => {
            if (query.length > 1) {
                const clientResults = staticPages.filter(p => p.name.toLowerCase().includes(query.toLowerCase()));
                try {
                    const serverResults = await globalSearch(query);
                    setSearchResults({
                        pages: clientResults,
                        products: serverResults.data.products,
                        customers: serverResults.data.customers,
                    });
                } catch (error) {
                    console.error("Search failed:", error);
                    setSearchResults({ pages: clientResults, products: [], customers: [] });
                }
            } else {
                setSearchResults(null);
            }
        }, 300),
        []
    );

    useEffect(() => { debouncedSearch(searchQuery); }, [searchQuery, debouncedSearch]);
    useEffect(() => { document.documentElement.setAttribute('data-bs-theme', theme); }, [theme]);

    // Unified storage sync for theme + user/avatar
    useEffect(() => {
        const handleLocalChange = () => {
            // Sync theme
            const storedTheme = localStorage.getItem('theme') || 'light';
            setTheme(storedTheme);
            document.documentElement.setAttribute('data-bs-theme', storedTheme);

            // Sync user
            try {
                const storedUser = localStorage.getItem('user');
                if (!storedUser || storedUser === "undefined") {
                    setUser(null);
                } else {
                    setUser(JSON.parse(storedUser));
                }
            } catch (e) {
                setUser(null);
            }
        };

        // Listen for same-tab custom event AND cross-tab native storage event
        window.addEventListener('localStorageChanged', handleLocalChange);
        window.addEventListener('storage', handleLocalChange);
        return () => {
            window.removeEventListener('localStorageChanged', handleLocalChange);
            window.removeEventListener('storage', handleLocalChange);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsSearchFocused(false);
            }
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                const input = searchRef.current?.querySelector('input');
                if (input) input.focus();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleSearchItemClick = (path) => {
        navigate(path);
        setSearchQuery('');
        setSearchResults(null);
        setIsSearchFocused(false);
    };

    const isActive = (path) => location.pathname === path ? 'active' : '';
    
    // Determine API URL based on environment for avatar
    // Use the backend-provided full URL or a fallback
    const avatarUrl = user?.profile_photo_url || `https://ui-avatars.com/api/?name=${user?.name || 'User'}&background=random`;

    return (
        <div className="d-flex">
            {/* Sidebar */}
            <div className={`sidebar-overlay ${showSidebar ? 'show' : ''}`} onClick={() => setShowSidebar(false)}></div>
            <div className={`sidebar ${showSidebar ? 'show' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="brand" style={{ padding: '0 20px', height: '70px', marginBottom: '20px' }}>
                    <div className="d-flex align-items-center gap-2 brand-content w-100 overflow-hidden">
                        {localStorage.getItem('company_logo') ? (
                            <img src={localStorage.getItem('company_logo')} alt="Logo" style={{ maxHeight: '40px', maxWidth: '140px', objectFit: 'contain' }} />
                        ) : (
                            <><span style={{ fontSize: '1.8rem' }}>🛍️</span> <span className="brand-text fw-bold">SmartShop</span></>
                        )}
                    </div>
                    <div className="collapse-toggle d-none d-md-flex align-items-center justify-content-center" onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
                        <i className={`bi bi-chevron-${isSidebarCollapsed ? 'right' : 'left'}`}></i>
                    </div>
                </div>

                <div className="sidebar-scrollable custom-scrollbar px-3">
                    {/* General Section */}
                    <div className="sidebar-section-label">General</div>
                    <Link to="/" className={`nav-link ${isActive('/')}`}><i className="bi bi-grid-1x2-fill"></i> <span className="nav-text">Dashboard</span></Link>
                    <Link to="/pos" className={`nav-link ${isActive('/pos')}`}><i className="bi bi-cart-check-fill"></i> <span className="nav-text">POS / Billing</span></Link>

                    {/* Management Section */}
                    <div className="sidebar-section-label mt-4">Management</div>
                    <Link to="/inventory" className={`nav-link ${isActive('/inventory')}`}><i className="bi bi-box-seam-fill"></i> <span className="nav-text">Inventory</span></Link>
                    <Link to="/categories" className={`nav-link ${isActive('/categories')}`}><i className="bi bi-tags-fill"></i> <span className="nav-text">Categories</span></Link>
                    <Link to="/customers" className={`nav-link ${isActive('/customers')}`}><i className="bi bi-people-fill"></i> <span className="nav-text">Customers</span></Link>
                    {localStorage.getItem('module_suppliers') === 'enabled' && (
                        <Link to="/suppliers" className={`nav-link ${isActive('/suppliers')}`}><i className="bi bi-truck-front-fill"></i> <span className="nav-text">Suppliers</span></Link>
                    )}
                    <Link to="/returns" className={`nav-link ${isActive('/returns')}`}><i className="bi bi-arrow-counterclockwise"></i> <span className="nav-text">Returns</span></Link>

                    {/* Finance & HR Section */}
                    <div className="sidebar-section-label mt-4">Finance & HR</div>
                    {localStorage.getItem('module_expenses') === 'enabled' && (
                        <Link to="/expenses" className={`nav-link ${isActive('/expenses')}`}><i className="bi bi-wallet2"></i> <span className="nav-text">Expenses</span></Link>
                    )}
                    {user?.role === 'admin' && (
                        <>
                            <Link to="/reports" className={`nav-link ${isActive('/reports')}`}><i className="bi bi-graph-up-arrow"></i> <span className="nav-text">Reports</span></Link>
                            <Link to="/reports/sold-products" className={`nav-link ${isActive('/reports/sold-products')}`}><i className="bi bi-box2-heart-fill"></i> <span className="nav-text">Sold Products</span></Link>
                        </>
                    )}
                    {(user?.role === 'admin' || user?.role === 'manager') && localStorage.getItem('module_team') !== 'disabled' && (
                        <Link to="/team" className={`nav-link ${isActive('/team')}`}><i className="bi bi-people-fill"></i> <span className="nav-text">Team Management</span></Link>
                    )}
                    <Link to="/employees" className={`nav-link ${isActive('/employees')}`}><i className="bi bi-person-badge-fill"></i> <span className="nav-text">Employee Portal</span></Link>
                    {(user?.role === 'admin' || user?.role === 'manager') && (
                        <Link to="/employee-salary" className={`nav-link ${isActive('/employee-salary')}`}><i className="bi bi-cash-stack"></i> <span className="nav-text">Payroll & HR</span></Link>
                    )}

                    {/* System Section */}
                    {user?.role === 'super_admin' && (
                        <>
                            <div className="sidebar-section-label mt-4">System</div>
                            <Link to="/super-admin" className={`nav-link ${isActive('/super-admin')}`}><i className="bi bi-shield-lock-fill text-primary"></i> <span className="nav-text text-primary fw-bold">Super Admin</span></Link>
                        </>
                    )}
                    {user?.role === 'admin' && (
                        <>
                            <div className="sidebar-section-label mt-4">System</div>
                            <Link to="/settings" className={`nav-link ${isActive('/settings')}`}><i className="bi bi-gear-fill"></i> <span className="nav-text">Settings</span></Link>
                        </>
                    )}
                </div>

                <div className="sidebar-footer mt-auto p-3">
                    <div className="user-profile-card d-flex align-items-center gap-3 p-2 mb-3" onClick={() => navigate('/profile')} style={{ cursor: 'pointer' }}>
                        <img src={avatarUrl} alt="User" className="rounded-circle" style={{ width: '35px', height: '35px', objectFit: 'cover' }} />
                        <div className="user-info overflow-hidden">
                            <div className="fw-bold text-truncate" style={{ fontSize: '0.85rem' }}>{user?.name}</div>
                            <div className="text-muted text-truncate text-uppercase" style={{ fontSize: '0.7rem' }}>{user?.role}</div>
                        </div>
                    </div>
                    <div className="btn-cta w-100" onClick={() => setShowSupportModal(true)} style={{ cursor: 'pointer' }}>
                        <i className="bi bi-headset me-2"></i>
                        <span className="nav-text text-truncate">Contact Support</span>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className={`main-content w-100 ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="top-header-modern sticky-top">
                    <div className="container-fluid px-0">
                        <div className="d-flex align-items-center justify-content-between header-inner">
                            {/* Left: Branding & Navigation Context */}
                            <div className="d-flex align-items-center gap-3 header-left">
                                <button className="btn btn-header-icon d-custom-drawer-mode" onClick={() => setShowSidebar(true)}>
                                    <i className="bi bi-list fs-4"></i>
                                </button>
                                <div className="d-none d-lg-block">
                                    <div className="d-flex align-items-center gap-2 mb-0 breadcrumb-modern">
                                        <Link to="/" className="text-muted small">Dashboard</Link>
                                        <i className="bi bi-chevron-right text-muted x-small"></i>
                                        <span className="fw-bold small text-primary text-uppercase ls-1">{title}</span>
                                    </div>
                                    <h4 className="fw-extrabold mb-0 page-title-animate">{title}</h4>
                                </div>
                            </div>

                            {/* Center: Intelligence Search & System Stats */}
                            <div className="flex-grow-1 mx-lg-5 d-custom-desktop-only header-center" ref={searchRef}>
                                <div className="modern-search-wrapper">
                                    <div className={`search-bar-modern ${isSearchFocused ? 'focused' : ''}`}>
                                        <i className="bi bi-search search-icon-animate"></i>
                                        <input 
                                            type="text" 
                                            placeholder="Command Search (Ctrl + K)..." 
                                            value={searchQuery} 
                                            onChange={(e) => setSearchQuery(e.target.value)} 
                                            onFocus={() => setIsSearchFocused(true)} 
                                        />
                                        <span className="search-shortcut">⌘K</span>
                                    </div>
                                    {isSearchFocused && searchQuery && (
                                        <div className="dropdown-menu show modern-search-results animate__animated animate__fadeInUp">
                                            {searchResults ? (
                                                <div className="search-scroll custom-scrollbar">
                                                    {searchResults.pages.length > 0 && (
                                                        <div className="search-group">
                                                            <div className="search-group-label">System Navigation</div>
                                                            {searchResults.pages.map(p => (
                                                                <button key={p.path} className="dropdown-item modern-item" onMouseDown={() => handleSearchItemClick(p.path)}>
                                                                    <div className="d-flex align-items-center gap-3">
                                                                        <div className="item-icon-box bg-primary-subtle text-primary"><i className="bi bi-file-earmark-text"></i></div>
                                                                        <span>{p.name}</span>
                                                                    </div>
                                                                    <i className="bi bi-arrow-right-short text-muted"></i>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {searchResults.products.length > 0 && (
                                                        <div className="search-group">
                                                            <div className="search-group-label">Inventory Items</div>
                                                            {searchResults.products.map(p => (
                                                                <button key={`prod-${p.id}`} className="dropdown-item modern-item" onMouseDown={() => handleSearchItemClick('/inventory')}>
                                                                    <div className="d-flex align-items-center gap-3">
                                                                        <div className="item-icon-box bg-success-subtle text-success"><i className="bi bi-box-seam"></i></div>
                                                                        <span>{p.name}</span>
                                                                    </div>
                                                                    <span className="badge bg-light text-dark fw-bold">Stock: {p.stock}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="p-4 text-center">
                                                    <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                                                    <span className="small fw-bold">Searching Command Database...</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right: Quick Actions & System Health */}
                            <div className="d-flex align-items-center gap-2 gap-lg-3 header-right">
                                {/* Quick Action Menu */}
                                <div className="dropdown d-none d-sm-block quick-actions-modern">
                                    <button className="btn btn-modern-action px-3" data-bs-toggle="dropdown">
                                        <i className="bi bi-lightning-charge-fill me-2 text-warning"></i>
                                        <span className="fw-bold small d-none d-lg-inline">QUICK ACTIONS</span>
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-end border-0 shadow-lg rounded-4 p-2 mt-2">
                                        <li><Link className="dropdown-item rounded-3 py-2" to="/pos"><i className="bi bi-cart-plus me-3 text-primary"></i>New Sale Invoice</Link></li>
                                        <li><Link className="dropdown-item rounded-3 py-2" to="/inventory"><i className="bi bi-plus-circle me-3 text-success"></i>Add Product</Link></li>
                                        <li><Link className="dropdown-item rounded-3 py-2" to="/customers"><i className="bi bi-person-plus me-3 text-info"></i>Add New Client</Link></li>
                                        <li><hr className="dropdown-divider opacity-50" /></li>
                                        <li><Link className="dropdown-item rounded-3 py-2" to="/reports"><i className="bi bi-bar-chart-line me-3 text-warning"></i>View Analytics</Link></li>
                                    </ul>
                                </div>

                                {/* Profile & Settings */}
                                <div className="position-relative" ref={dropdownRef}>
                                    <div className="profile-trigger-modern" onClick={() => setShowDropdown(!showDropdown)}>
                                        <div className="avatar-wrapper">
                                            <img src={avatarUrl} alt="User" />
                                            <div className="status-dot-active"></div>
                                        </div>
                                        <div className="d-none d-md-block ms-2">
                                            <div className="fw-bold small text-truncate" style={{ maxWidth: '100px' }}>{user?.name}</div>
                                            <div className="x-small text-muted text-uppercase fw-bold ls-1">{user?.role}</div>
                                        </div>
                                        <i className={`bi bi-chevron-down ms-2 x-small transition-all ${showDropdown ? 'rotate-180' : ''}`}></i>
                                    </div>

                                    {showDropdown && (
                                        <div className="dropdown-menu dropdown-menu-end show modern-profile-dropdown animate__animated animate__fadeIn">
                                            <div className="dropdown-header-custom p-3 border-bottom mb-2">
                                                <div className="fw-bold mb-0">{user?.name}</div>
                                                <div className="small text-muted">{user?.email}</div>
                                            </div>
                                            <li><Link className="dropdown-item rounded-3" to="/profile"><i className="bi bi-person-circle me-3"></i>My Profile</Link></li>
                                            <li><Link className="dropdown-item rounded-3" to="/account-settings"><i className="bi bi-gear-fill me-3"></i>Security Settings</Link></li>
                                            <li>
                                                <div className="dropdown-item rounded-3 d-flex justify-content-between align-items-center" onClick={toggleTheme} style={{ cursor: 'pointer' }}>
                                                    <span><i className={`bi bi-${theme === 'light' ? 'moon-stars-fill' : 'sun-fill'} me-3`}></i>Appearance</span>
                                                    <div className="form-check form-switch m-0"><input className="form-check-input" type="checkbox" checked={theme === 'dark'} readOnly /></div>
                                                </div>
                                            </li>
                                            <li><hr className="dropdown-divider opacity-50" /></li>
                                            <li><button className="dropdown-item rounded-3 text-danger fw-bold" onClick={handleLogout}><i className="bi bi-power me-3"></i>Terminate Session</button></li>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="page-content">{children}</div>
            </div>

            {/* Contact Support Modal */}
            {showSupportModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={(e) => e.target === e.currentTarget && setShowSupportModal(false)}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                            <div className="modal-header border-0 pt-4 px-4">
                                <div>
                                    <h5 className="modal-title fw-bold mb-1"><i className="bi bi-headset me-2 text-primary"></i>Contact Support</h5>
                                    <small className="text-muted">We'll get back to you as soon as possible</small>
                                </div>
                                <button className="btn-close" onClick={() => setShowSupportModal(false)}></button>
                            </div>
                            <div className="modal-body px-4 pb-2">
                                <div className="mb-3">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Category</label>
                                    <select className="form-select" value={supportForm.category} onChange={e => setSupportForm({...supportForm, category: e.target.value})}>
                                        <option value="">Select a category...</option>
                                        <option value="Bug Report">🐛 Bug Report</option>
                                        <option value="Feature Request">💡 Feature Request</option>
                                        <option value="Billing">💳 Billing</option>
                                        <option value="Account Issue">🔐 Account Issue</option>
                                        <option value="Other">📝 Other</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Subject</label>
                                    <input type="text" className="form-control" placeholder="Brief summary of the issue" value={supportForm.subject} onChange={e => setSupportForm({...supportForm, subject: e.target.value})} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Describe Your Issue</label>
                                    <textarea className="form-control" rows="5" placeholder="Please describe the issue you're experiencing in detail..." value={supportForm.message} onChange={e => setSupportForm({...supportForm, message: e.target.value})} style={{ resize: 'none', height: '120px' }}></textarea>
                                </div>
                            </div>
                            <div className="modal-footer border-0 px-4 pb-4 pt-0">
                                <div className="row w-100 g-2">
                                    <div className="col-6 m-0">
                                        <button className="btn btn-light border w-100 fw-bold" onClick={() => setShowSupportModal(false)} style={{ height: '50px', borderRadius: '12px' }}>Cancel</button>
                                    </div>
                                    <div className="col-6 m-0">
                                        <button className="btn btn-primary w-100 fw-bold shadow-sm" onClick={handleSupportSubmit} disabled={supportLoading} style={{ height: '50px', borderRadius: '12px' }}>
                                            {supportLoading ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending...</> : <><i className="bi bi-send-fill me-2"></i>Submit Ticket</>}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Layout;
